import { useLayoutEffect, useRef } from "react";
import { getPreviewBounds, placePreview } from "./previewPlacement";

export default function usePreviewPlacement(
  modalRef,
  vodData,
  enabled,
  onDismiss,
  maxWidth
) {
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  useLayoutEffect(() => {
    const modal = modalRef.current;
    if (!enabled || !modal || !vodData?.position) return;
    const initialScrollY = window.scrollY;
    let frame;
    let dismissed = false;
    const dismiss = () => {
      if (dismissed) return;
      dismissed = true;
      dismissRef.current();
    };
    const update = () => {
      if (dismissed) return;
      const viewport = window.visualViewport;
      const bounds = getPreviewBounds({
        maxWidth,
        width:
          viewport?.width ||
          document.documentElement.clientWidth ||
          window.innerWidth,
        height: viewport?.height || window.innerHeight,
        top: viewport?.offsetTop || 0,
        left: viewport?.offsetLeft || 0,
        navbarBottom:
          document.querySelector(".atlas-navbar")?.getBoundingClientRect()
            .bottom || 0
      });
      const element = vodData.anchorElement;
      const fallback = vodData.position;
      const anchor = element?.getBoundingClientRect() || {
        width: fallback.width,
        height: fallback.height,
        left:
          (fallback.left ?? fallback.x) -
          (window.scrollX - (vodData.scrollX || 0)),
        top:
          (fallback.top ?? fallback.y) -
          (window.scrollY - (vodData.scrollY ?? initialScrollY))
      };
      if (
        window.innerWidth < 1000 ||
        (element && !element.isConnected) ||
        anchor.top + anchor.height <= bounds.top ||
        anchor.top >= bounds.bottom ||
        anchor.left + anchor.width <= bounds.left ||
        anchor.left >= bounds.right ||
        Math.abs(window.scrollY - initialScrollY) > 160
      ) {
        dismiss();
        return;
      }
      // Measure at the final width, independently of the entrance scale animation.
      modal.style.width = `${bounds.width}px`;
      modal.style.maxHeight = `${bounds.maxHeight}px`;
      const placement = placePreview(anchor, bounds, modal.scrollHeight);
      modal.style.left = `${placement.left}px`;
      modal.style.top = `${placement.top}px`;
      const origin = Math.max(
        0,
        Math.min(
          100,
          ((anchor.left + anchor.width / 2 - placement.left) / bounds.width) *
            100
        )
      );
      modal.style.transformOrigin = `${origin}% center`;
    };
    const scheduleUpdate = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);
    };
    const onWheel = (event) => {
      // This preview is portalled outside its shelf, so native wheel bubbling
      // cannot reach the horizontal track underneath it.
      if (event.ctrlKey || Math.abs(event.deltaX) <= Math.abs(event.deltaY))
        return;
      const track = vodData.anchorElement?.closest(".cinema-scroll-track");
      if (!track?.isConnected) return;
      const unit =
        event.deltaMode === 1
          ? parseFloat(getComputedStyle(track).lineHeight) || 16
          : event.deltaMode === 2
          ? track.clientWidth
          : 1;
      event.preventDefault();
      track.scrollLeft += event.deltaX * unit;
      dismiss();
    };
    update();
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleUpdate)
        : null;
    if (observer) {
      observer.observe(modal.firstElementChild);
      const navbar = document.querySelector(".atlas-navbar");
      if (navbar) observer.observe(navbar);
    }
    window.addEventListener("scroll", scheduleUpdate, true);
    window.addEventListener("resize", scheduleUpdate);
    modal.addEventListener("wheel", onWheel, { passive: false });
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", scheduleUpdate);
      window.visualViewport.addEventListener("scroll", scheduleUpdate);
    }
    return () => {
      window.cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
      window.removeEventListener("scroll", scheduleUpdate, true);
      window.removeEventListener("resize", scheduleUpdate);
      modal.removeEventListener("wheel", onWheel);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", scheduleUpdate);
        window.visualViewport.removeEventListener("scroll", scheduleUpdate);
      }
    };
  }, [modalRef, vodData, enabled, maxWidth]);
}
